import { APIClassReference, MemberType, ReadWriteSecurityTagsObj } from '@modules/client';
import { SecurityTag } from '@modules/ui/tags/types';
import {
  isClassReference,
  isDatatypeReference,
  isGlobalReference,
  isLibraryReference,
  isParentClassReference,
} from './apiReferenceUtils';
import { Reference, EnumReference, ParentClassReference } from '../types';

export default function getApiMemberTypeId({
  referenceType,
  apiName,
  memberName,
}: {
  referenceType: string;
  apiName: string;
  memberName: string;
}): string {
  if (!referenceType || !apiName || !memberName) {
    throw new Error('referenceType, apiName, and memberName are required');
  }
  return `${referenceType}.${apiName}.${memberName}`;
}

export const getReferenceSummaryMemberTableMembers = (
  reference: Exclude<Reference, EnumReference> | ParentClassReference | APIClassReference,
  memberType: MemberType,
) => {
  // TODO: clean up reference types
  // ref: https://github.rbx.com/Roblox/creator-doc-site/pull/685#discussion_r995906
  if (isParentClassReference(reference) || isClassReference(reference)) {
    switch (memberType) {
      case MemberType.Callback:
        return reference.callbacks;
      case MemberType.Event:
        return reference.events;
      case MemberType.Method:
        return reference.methods;
      case MemberType.Property:
        return reference.properties;
      default:
        throw new Error(`Unhandled class reference member type: ${memberType}`);
    }
  }
  if (isDatatypeReference(reference)) {
    switch (memberType) {
      case MemberType.Constructor:
        return reference.constructors;
      case MemberType.Property:
        return reference.properties;
      case MemberType.Method:
        return reference.methods;
      case MemberType.Function:
        return reference.functions;
      case MemberType.MathOperation:
        return reference.mathOperations;
      default:
        throw new Error(`Unhandled datatype reference member type: ${memberType}`);
    }
  }
  if (isGlobalReference(reference) || isLibraryReference(reference)) {
    switch (memberType) {
      case MemberType.Property:
        return reference.properties;
      case MemberType.Function:
        return reference.functions;
      default:
        throw new Error(`Unhandled global/lib reference member type: ${memberType}`);
    }
  }
  throw new Error(`Unhandled member type ${memberType} for reference ${JSON.stringify(reference)}`);
};

export const getReferenceSummaryMemberTableInheritedMembers = (
  orderedParentReferences: ParentClassReference[],
  memberType: MemberType,
): Record<string, ReturnType<typeof getReferenceSummaryMemberTableMembers>> => {
  if (!orderedParentReferences.length) return {};
  return orderedParentReferences.reduce((accum, parentReference) => {
    return {
      ...accum,
      [parentReference.name]: getReferenceSummaryMemberTableMembers(parentReference, memberType),
    };
  }, {});
};

/**
 * Normalizes security tags from different formats into a consistent SecurityTag[] | null format.
 * Handles three cases:
 * 1. null/undefined → null
 * 2. Array (methods/events/callbacks) → returns as-is
 * 3. Object with read/write (properties) → converts to array, deduplicating if read === write
 */
export const normalizeSecurityTags = (
  security: SecurityTag[] | ReadWriteSecurityTagsObj | null | undefined,
): SecurityTag[] | null => {
  if (!security) {
    return null;
  }

  if (Array.isArray(security)) {
    return security;
  }

  // Handle read/write object format (properties)
  if (security.read !== null || security.write !== null) {
    const securities: SecurityTag[] = [];

    // If read and write are the same, only add once to avoid duplication
    if (security.read !== null && security.read === security.write) {
      securities.push(security.read);
    } else {
      if (security.read !== null) securities.push(security.read);
      if (security.write !== null) securities.push(security.write);
    }

    return securities;
  }

  return null;
};
